import React, { FC, useState, useContext, useEffect } from "react";
import { Table, Tooltip, Input } from "antd";
import {
  LinkOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ExternalMatterData, ExternalMatter, PracticeArea } from "../../data-types";
import { LinkExternalMatterModal } from "../link-matter-modal";
import { CreateMatterFromPmsModal } from "./create-matter-from-pms-modal";
import { GlobalContext } from "~/context/global-context";
import { FindCumulativeAccruedFeesForExternalMatterQuery } from "~/gql/graphql";
import styles from "./pms-matters.css";
import { LoadingSkeleton } from "../../../components/page-loading/skeleton/loading-skeleton";

const { ["table-cell"]: tableCellClassName } = styles;

export type PmsMattersProps = {
  externalMatterData: ExternalMatterData;
  loading: boolean;
  practiceAreas: PracticeArea[];
  pagination: { total: number; current: number; pageSize: number };
  setPage: (page: number) => void;
  onLinkExternalMatterToMatter: (
    externalMatterId: number | undefined,
    matterId: number
  ) => Promise<void>;
  onCreateMatterFromPms: (
    externalMatter: ExternalMatter,
    budget: number,
    organisationalUnitId: number
  ) => Promise<void>;
  pmsName: string;
  getCumulativeAccruedFeesForExternalMatter: (externalMatterId: number) => void;
  cumulativeAccruedFeesData: FindCumulativeAccruedFeesForExternalMatterQuery | undefined;
  search: string | undefined;
  onSearch: (searchTerm: string | undefined) => void;
};

export const PmsMatters: FC<PmsMattersProps> = ({
  externalMatterData,
  loading,
  practiceAreas,
  pagination,
  setPage,
  onLinkExternalMatterToMatter,
  onCreateMatterFromPms,
  pmsName,
  getCumulativeAccruedFeesForExternalMatter,
  cumulativeAccruedFeesData,
  search,
  onSearch,
}) => {
  const [showLinkMatterModal, setShowLinkMatterModal] = useState(false);
  const [showCreateMatterFromPmsModal, setShowCreateMatterFromPmsModal] = useState(false);
  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(null);
  const [localSearchValue, setLocalSearchValue] = useState(search || "");
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const currentUserInfo = useContext(GlobalContext);

  // Update local search value when prop changes
  useEffect(() => {
    setLocalSearchValue(search || "");
  }, [search]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const onChangeHandler = (pagination: { current?: number; pageSize?: number }) => {
    setPage(pagination.current ?? 1);
  };

  useEffect(() => {
    if (selectedExternalMatter) {
      getCumulativeAccruedFeesForExternalMatter(selectedExternalMatter.id);
    }
  }, [selectedExternalMatter]);

  if (loading) {
    return (
      <div style={{ marginTop: "55px" }}>
        <LoadingSkeleton count={5} loading={loading} active />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          padding: "var(--padding-md)",
          background: "var(--component-background)",
          borderBottom: "1px solid var(--border-color-base)",
        }}
      >
        <Input
          placeholder="Search by client name, matter title, or matter reference..."
          prefix={<SearchOutlined style={{ color: "var(--text-color-secondary)" }} />}
          value={localSearchValue}
          onChange={(e) => {
            const value = e.target.value;
            setLocalSearchValue(value);

            // Clear existing timeout
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }

            // Debounce search - wait 500ms after user stops typing
            searchTimeoutRef.current = setTimeout(() => {
              if (value.trim() === "") {
                onSearch(undefined);
              } else {
                onSearch(value);
              }
            }, 500);
          }}
          allowClear
          style={{
            maxWidth: "500px",
            borderRadius: "var(--border-radius-sm)",
          }}
        />
      </div>
      {/* Table for displaying external matters */}
      <Table
        style={externalMatterData.data?.items?.length === 0 ? { marginTop: "55px" } : undefined}
        onChange={(pagination) => {
          return onChangeHandler(pagination);
        }}
        dataSource={externalMatterData.data?.items ?? []}
        columns={[
          {
            title: "Date Created",
            dataIndex: "externalCreatedAt",
            key: "externalCreatedAt",
            render: (createdAt: string) => {
              const formattedDate = new Date(createdAt).toLocaleDateString();
              return <div>{formattedDate}</div>;
            },
            width: "12%",
          },
          {
            title: "Matter Title",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
          },
          {
            title: "Client",
            dataIndex: ["client", "name"],
            key: "client",
            ellipsis: true,
          },
          {
            title: "Matter Reference",
            dataIndex: "externalReference",
            key: "externalReference",
            ellipsis: true,
            width: "15%",
          },
          {
            title: (
              <>
                Link{" "}
                <Tooltip
                  placement="top"
                  title={"Use this option if you already have a proposal in VPD for this matter."}
                >
                  <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
                </Tooltip>
              </>
            ),
            key: "link",
            align: "center",
            width: "10%",
            render: (record: ExternalMatter) => (
              <div className={tableCellClassName}>
                <LinkOutlined
                  onClick={() => {
                    setSelectedExternalMatter(record);
                    setShowLinkMatterModal(true);
                  }}
                />
              </div>
            ),
          },
          {
            title: (
              <>
                Track
                <Tooltip
                  placement="top"
                  title={`Use this option if you want to add this ${pmsName} matter to your My Live Matters for GPM tracking and you did not create a proposal for it in VPD previously. VPD will create a proposal with the budget you enter and link it to his matter. You will have full ability to track it .`}
                >
                  <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
                </Tooltip>
              </>
            ),
            key: "createProposal",
            align: "center",
            width: "15%",
            render: (record: ExternalMatter) => (
              <div className={tableCellClassName}>
                <FileAddOutlined
                  onClick={() => {
                    setSelectedExternalMatter(record);
                    setShowCreateMatterFromPmsModal(true);
                  }}
                />
              </div>
            ),
          },
        ]}
        pagination={{
          ...pagination,
          position: ["topRight"],
          simple: true,
        }}
      />
      {currentUserInfo.currentUser?.userId && (
        <>
          <LinkExternalMatterModal
            open={showLinkMatterModal}
            externalMatter={selectedExternalMatter}
            onClose={() => setShowLinkMatterModal(false)}
            onLinkExternalMatterToMatter={onLinkExternalMatterToMatter}
            currentUserId={currentUserInfo.currentUser?.userId}
            pmsName={pmsName}
          />
          <CreateMatterFromPmsModal
            open={
              showCreateMatterFromPmsModal &&
              !!cumulativeAccruedFeesData?.findCumulativeAccruedFeesForExternalMatter
            }
            onClose={() => setShowCreateMatterFromPmsModal(false)}
            onCreateMatterFromPms={onCreateMatterFromPms}
            externalMatter={selectedExternalMatter}
            practiceAreas={practiceAreas}
            cumulativeAccruedFee={
              cumulativeAccruedFeesData?.findCumulativeAccruedFeesForExternalMatter
                ?.map((item) => item.amount)
                .slice(-1)[0] ?? 0
            }
          />
        </>
      )}
    </>
  );
};
